import React, { useState, useEffect, useMemo } from 'react';
import { getRecords } from '../utils/storage';
import { getLogs } from '../services/readingLogsApi';
import BottomNav from './BottomNav';

const MEDALS = ['🥇', '🥈', '🥉'];

function starStr(n) {
  const s = Math.min(Math.max(n ?? 0, 0), 3);
  return '⭐'.repeat(s) + '☆'.repeat(3 - s);
}

export default function RankingScreen({ onNav, isPremium, childId, isGuest, currentChild, children }) {
  const [tab, setTab] = useState('month'); // 'month' | 'book'
  const [showAll, setShowAll] = useState(true); // true=全員 false=自分だけ
  const [records, setRecords] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isGuest && children?.length > 0) {
        // 全兄弟のきろくをまとめて取得
        const allLogs = await Promise.all(
          children.map(async (child) => {
            const logs = await getLogs(child.id);
            // 各きろくにchild情報を付与
            return (logs || []).map(r => ({ ...r, childId: child.id, childName: child.name, childAvatar: child.avatar }));
          })
        );
        if (!cancelled) setRecords(allLogs.flat());
      } else {
        if (!cancelled) setRecords(getRecords());
      }
    }
    load();
    return () => { cancelled = true; };
  }, [isGuest, children]);

  const thisMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

  // 今月のランキング（スコア降順 全件）
  const monthRankingAll = useMemo(() => {
    return records
      .filter((r) => r.localDate?.startsWith(thisMonth))
      .slice()
      .sort((a, b) => (b.avgScore ?? 0) - (a.avgScore ?? 0));
  }, [records, thisMonth]);

  // 作品別ベストスコア（作品ごとにグループ化、各作品内で兄弟スコア降順）
  const bookRankingGrouped = useMemo(() => {
    const byTitleChild = {};
    records.forEach((r) => {
      if (!r.title) return;
      const key = `${r.title}__${r.childName}`;
      if (!byTitleChild[key] || (r.avgScore ?? 0) > (byTitleChild[key].avgScore ?? 0)) {
        byTitleChild[key] = r;
      }
    });
    // タイトルごとにグループ化
    const grouped = {};
    Object.values(byTitleChild).forEach((r) => {
      if (!grouped[r.title]) grouped[r.title] = [];
      grouped[r.title].push(r);
    });
    // 各グループ内をスコア降順にソート
    // グループ自体はその中の最高スコア降順で並べる
    return Object.entries(grouped)
      .map(([title, entries]) => ({
        title,
        entries: entries.sort((a, b) => (b.avgScore ?? 0) - (a.avgScore ?? 0)),
        bestScore: Math.max(...entries.map(e => e.avgScore ?? 0)),
      }))
      .sort((a, b) => b.bestScore - a.bestScore);
  }, [records]);

  const isMultiChild = !isGuest && children?.length > 1;

  // 自分だけ表示のときはchildIdでフィルタ（top10制限なし）
  // 全員表示のときはtop10 ＋ top10に入っていない兄弟の最高記録を追加
  const filteredMonthRanking = useMemo(() => {
    if (isMultiChild && !showAll) {
      // 自分だけ：全件から自分の記録top10
      return monthRankingAll
        .filter(r => r.childId === childId || (!r.childId && r.childName === currentChild?.name))
        .slice(0, 10);
    }
    // 全員：top10
    const top10 = monthRankingAll.slice(0, 10);
    if (!isMultiChild) return top10;
    // top10に入っていない兄弟の最高記録を追加
    const representedIds = new Set(top10.map(r => r.childId || r.childName));
    const missing = [];
    (children || []).forEach(child => {
      const id = child.id;
      const name = child.name;
      if (representedIds.has(id) || representedIds.has(name)) return;
      // この子の最高記録を探す
      const best = monthRankingAll.find(r => r.childId === id || (!r.childId && r.childName === name));
      if (best) missing.push({ ...best, _missingRank: monthRankingAll.indexOf(best) + 1 });
    });
    return missing.length > 0 ? [...top10, ...missing] : top10;
  }, [isMultiChild, showAll, monthRankingAll, childId, currentChild, children]);

  // 作品別：自分だけのときは自分のエントリだけに絞る
  const filteredBookRankingGrouped = useMemo(() => {
    if (isMultiChild && !showAll) {
      return bookRankingGrouped
        .map(g => ({
          ...g,
          entries: g.entries.filter(r => r.childId === childId || (!r.childId && r.childName === currentChild?.name)),
        }))
        .filter(g => g.entries.length > 0);
    }
    return bookRankingGrouped;
  }, [isMultiChild, showAll, bookRankingGrouped, childId, currentChild]);

  const list = filteredMonthRanking; // 今月ランキング用（bookはfilteredBookRankingGroupedを直接使う）

  return (
    <div className="screen ranking-screen">

      {/* ─── ヘッダー ─── */}
      <div className="v9-header" style={{ width: '100%', flexShrink: 0 }}>
        <div className="v9-blob1" />
        <div className="v9-blob2" />
        <div className="v9-header-inner">
          <div className="v9-header-top">
            <div>
              <div className="v9-logo-sub">READING APP</div>
              <div className="v9-logo-main">Ondoku <span>Quest</span></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {currentChild && (
                <span style={{ color: 'white', fontSize: 16, fontWeight: 700, opacity: 0.9 }}>
                  {currentChild.avatar} {currentChild.name}
                </span>
              )}
              <button
                onClick={() => onNav('home')}
                style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 24, color: 'white', fontSize: 13, fontWeight: 700, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit' }}
              >← ホーム</button>
            </div>
          </div>
          <div className="v9-greeting-msg" style={{ marginBottom: 0 }}>
            {isMultiChild
              ? <>きょうだいで　<span>きそおう</span>！</>
              : <>こうとくてんを　<span>めざそう</span>！</>
            }
          </div>
        </div>
      </div>

      {/* ─── 全員/自分切り替え（兄弟が複数いるときのみ） ─── */}
      {isMultiChild && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 16px 0' }}>
          <div style={{ display: 'flex', background: '#F3E8FF', borderRadius: 20, padding: 3, gap: 3 }}>
            <button
              onClick={() => setShowAll(true)}
              style={{ background: showAll ? '#7C3AED' : 'transparent', color: showAll ? 'white' : '#7C3AED', border: 'none', borderRadius: 17, padding: '6px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}
            >👨‍👧‍👦 きょうだい全員</button>
            <button
              onClick={() => setShowAll(false)}
              style={{ background: !showAll ? '#7C3AED' : 'transparent', color: !showAll ? 'white' : '#7C3AED', border: 'none', borderRadius: 17, padding: '6px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}
            >{currentChild?.avatar} {currentChild?.name}だけ</button>
          </div>
        </div>
      )}

      {/* ─── タブ ─── */}
      <div className="ranking-tabs">
        <button
          className={`ranking-tab${tab === 'month' ? ' active' : ''}`}
          onClick={() => setTab('month')}
        >
          今月のランキング
        </button>
        <button
          className={`ranking-tab${tab === 'book' ? ' active' : ''}`}
          onClick={() => setTab('book')}
        >
          作品別ベスト
        </button>
      </div>

      {/* ─── ランキングリスト ─── */}
      <div className="ranking-list">
        {/* ゲスト：クラウド同期の案内 */}
        {isGuest && (
          <div className="record-limit-banner">
            📱 ゲストのきろくはこの端末のみ保存されます
          </div>
        )}

        {/* ── 今月のランキング ── */}
        {tab === 'month' && (list.length === 0 ? (
          <div className="record-empty">
            <div className="record-empty-icon">🏆</div>
            <p className="record-empty-text">まだ　今月の　きろくが　ありません</p>
            <p className="record-empty-sub">おんどくして　ランキングに　のろう！</p>
          </div>
        ) : (
          list.map((rec, i) => (
            <React.Fragment key={rec.id || i}>
              {rec._missingRank && (
                <div style={{ textAlign: 'center', color: '#aaa', fontSize: 12, padding: '4px 0', letterSpacing: 2 }}>・・・</div>
              )}
              <div className="ranking-item">
                <div className="ranking-rank">
                  {rec._missingRank
                    ? <span className="ranking-num">{rec._missingRank}</span>
                    : i < 3 ? <span className="ranking-medal">{MEDALS[i]}</span> : <span className="ranking-num">{i + 1}</span>
                  }
                </div>
                <div className="ranking-info">
                  <div className="ranking-title">『{rec.title}』</div>
                  <div className="ranking-meta">
                    {isMultiChild && rec.childName && (
                      <span style={{ fontWeight: 700 }}>{rec.childAvatar} {rec.childName}</span>
                    )}
                    <span className={`record-mode-badge ${rec.mode === 'honban' ? 'mode-honban' : 'mode-renshyu'}`}>
                      {rec.mode === 'honban' ? 'ほんばん' : 'れんしゅう'}
                    </span>
                    <span className="ranking-stars">{starStr(rec.totalStars)}</span>
                    <span className="ranking-date">{rec.localDate?.slice(5).replace('-', '月') + '日'}</span>
                  </div>
                </div>
                <div className="ranking-score-wrap">
                  <div className="ranking-score">{rec.avgScore ?? '—'}</div>
                  <div className="ranking-score-label">てん</div>
                </div>
              </div>
            </React.Fragment>
          ))
        ))}

        {/* ── 作品別ベスト（タイトルごとにグループ表示） ── */}
        {tab === 'book' && (filteredBookRankingGrouped.length === 0 ? (
          <div className="record-empty">
            <div className="record-empty-icon">🏆</div>
            <p className="record-empty-text">まだ　きろくが　ありません</p>
            <p className="record-empty-sub">おんどくして　ランキングに　のろう！</p>
          </div>
        ) : (
          filteredBookRankingGrouped.map((group) => (
            <div key={group.title} style={{ marginBottom: 12 }}>
              {/* タイトルヘッダー */}
              <div style={{
                background: 'linear-gradient(90deg, #7C3AED22, transparent)',
                borderLeft: '3px solid #7C3AED',
                padding: '6px 12px',
                fontWeight: 700,
                fontSize: 13,
                color: '#5B21B6',
                marginBottom: 4,
              }}>
                📖 『{group.title}』
              </div>
              {/* その作品内の兄弟スコア */}
              {group.entries.map((rec, j) => (
                <React.Fragment key={rec.id || j}>
                  {/* 3位と4位の間に区切り */}
                  {j === 3 && (
                    <div style={{ textAlign: 'center', color: '#aaa', fontSize: 11, padding: '2px 0 4px', letterSpacing: 2 }}>── ランク外 ──</div>
                  )}
                <div className="ranking-item" style={{ paddingLeft: 16, opacity: j >= 3 ? 0.7 : 1 }}>
                  <div className="ranking-rank">
                    {j < 3 ? <span className="ranking-medal">{MEDALS[j]}</span> : <span className="ranking-num">{j + 1}</span>}
                  </div>
                  <div className="ranking-info">
                    <div className="ranking-meta">
                      {isMultiChild && rec.childName && (
                        <span style={{ fontWeight: 700 }}>{rec.childAvatar} {rec.childName}</span>
                      )}
                      <span className={`record-mode-badge ${rec.mode === 'honban' ? 'mode-honban' : 'mode-renshyu'}`}>
                        {rec.mode === 'honban' ? 'ほんばん' : 'れんしゅう'}
                      </span>
                      <span className="ranking-stars">{starStr(rec.totalStars)}</span>
                    </div>
                  </div>
                  <div className="ranking-score-wrap">
                    <div className="ranking-score">{rec.avgScore ?? '—'}</div>
                    <div className="ranking-score-label">てん</div>
                  </div>
                </div>
                </React.Fragment>
              ))}
            </div>
          ))
        ))}

      </div>

      <BottomNav active="ranking" onNav={onNav} />
    </div>
  );
}
